import { useMessage } from "@hermes/intl";
import { useEffect, useRef, useState } from "react";

type Options = {
  words: string[];
  lines?: number;
  fontSize?: number;
  lineHeight?: number;
  fontFamily?: string;
  enable?: boolean;
};

export const useVisibleWordsFromDOM = ({
  words,
  lines = 2,
  fontSize = 16,
  lineHeight = 20,
  fontFamily = "ProximaNova, sans-serif",
  enable,
}: Options) => {
  const message = useMessage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleWords, setVisibleWords] = useState<string[]>([]);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    if (!enable) return;
    const measure = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      if (!containerWidth) return;

      const tempSpan = document.createElement("span");
      tempSpan.style.position = "absolute";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.pointerEvents = "none";
      tempSpan.style.whiteSpace = "normal";
      tempSpan.style.display = "block";
      tempSpan.style.width = `${containerWidth}px`;
      tempSpan.style.fontSize = `${fontSize}px`;
      tempSpan.style.lineHeight = `${lineHeight}px`;
      tempSpan.style.fontFamily = fontFamily;
      tempSpan.style.wordBreak = "break-word";
      document.body.appendChild(tempSpan);

      let result: string[] = [];
      for (let i = 0; i < words.length; i++) {
        const testWords = [...result, words[i]];
        tempSpan.innerText =
          testWords.join(", ") + `, ${message({ id: "common.btn.show-more" })}`;

        const computedLineHeight = parseFloat(
          getComputedStyle(tempSpan).lineHeight,
        );
        const tempLines = Math.round(
          tempSpan.clientHeight / computedLineHeight,
        );
        if (tempLines > lines) break;
        result.push(words[i] || "");
      }

      setVisibleWords(result);
      setIsClamped(result.length < words.length);

      document.body.removeChild(tempSpan);
    };

    requestAnimationFrame(measure);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [words, lines, enable, fontSize, lineHeight, fontFamily]);

  return { containerRef, visibleWords, isClamped };
};
